import { autoinject } from "aurelia-dependency-injection";
import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import { Urls } from '../../constants/queries/query.constants';
import {
    DataType,
    EntityMetaDataModel,
    EntityMetaDataModelOperator,
    GetAllQueriesRequest,
    GetQueriesRequest,
    GetQueriesResponse,
    ODataResponse,
    PersistQueryRequest,
    Query,
    UpdateODataResponse,
    QueryAssignmentBulkItem,
    CloneQueryRequest,
    QueryAssignmentEnum,
    AddQueryAssignmentRequest,
    DeleteAssignmentsForQueryRequest,
    PersistQueryResponse,
    DeleteQueryResponse,
    EntityFilterPropertyModel,
    GetOperatorsResponse
} from '../../models/queries/queries.models';
import { EnumUtilities } from "../../utilities/enum-utilities";
import { StringUtilities } from '../../utilities/strings';
import { EntityTypeConfigurationModel } from '../../models/basics/basics.models';
import { CloneHelper } from '@rightship-tech/common-ui/helpers/clone.helper';
import { Field } from '../../views/components/queries/field';
import { UserClientService } from '../authentication/user-client.service';
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";

@autoinject
export class QueriesClientService {

    private _entityMetadataPromise: Promise<EntityMetaDataModel[]>;
    private _entityMetaDataModelOperatorsPromise: Promise<EntityMetaDataModelOperator[]>;

    constructor(private requestClient: RequestClientService,
        private tokenClient: UserClientService,
        private configService: ConfigService) {
    }

    public clearCache(): Promise<void> {
        return new Promise<void>((resolve) => {
            this._entityMetadataPromise = null;
            resolve();
        });
    }

    public getEntityModels(): Promise<EntityMetaDataModel[]> {

        if (!this._entityMetadataPromise) {
            this._entityMetadataPromise = this.requestClient.get<any>(Urls.EntityMetaData, null);
        }
        return this._entityMetadataPromise;
    }

    public getData(url: string, trackingId: string = null): Promise<any> {
        const promise = new Promise<any[]>((resolve, reject) => {

            const urlParts = url.split("?");

            let baseUrl = urlParts[0] + "/$query";

            if (trackingId) {
                baseUrl += "?tracking_id=" + trackingId;
            }

            if (baseUrl.indexOf("/") != 0 && !baseUrl.startsWith("http")) {
                baseUrl = "/" + baseUrl;
            }

            const fallbackUrl = this.configService.config.urls.rightShipPlatformApiUrl;

            const fullUrl = baseUrl.startsWith("http") ? baseUrl : fallbackUrl + baseUrl;
            this.requestClient.post<any>(`${fullUrl}`, {}, urlParts[1] || "")
                .then((result: any) => {
                    if (result.value && result.value[0] &&
                        result.value[0]["groupedResult"]) {
                        if (result.value[0] && !result.value[0].dynamicProperties) {
                            delete result.value[0].dynamicProperties;
                        }
                        if (result.value[0]["groupedResult"].length > 0) {
                            result.value[0]["groupedResult"].forEach(x => {
                                result.value[0][x["key"]] = x["data"];
                            });
                        }

                        delete result.value[0]["groupedResult"];
                    }
                    resolve(result);
                }, (error) => {
                    reject(error);
                });
        });
        return promise;
    }

    public getQueryOperators(): Promise<GetOperatorsResponse> {
        return new Promise<GetOperatorsResponse>((resolve, reject) => {
            if (this._entityMetaDataModelOperatorsPromise) {
                this._entityMetaDataModelOperatorsPromise.then((result: any) => {
                    resolve(this.mapOperators(CloneHelper.cloneObject(result)));
                }, (error) => {
                    reject(error);
                });
            } else {
                this._entityMetaDataModelOperatorsPromise = this.requestClient.get(Urls.EntityMetaDataOperators, null);
                this._entityMetaDataModelOperatorsPromise.then((result: any) => {
                    resolve(this.mapOperators(CloneHelper.cloneObject(result)));
                }, (error) => {
                    reject(error);
                });
            }
        });
    }

    public getQueriesForUser(entityType: string): Promise<GetQueriesResponse> {
        return this.requestClient.get(Urls.Queries, new GetQueriesRequest(entityType));
    }

    public getQueries(entityType: string[]): Promise<GetQueriesResponse> {
        return this.requestClient.get(Urls.QueriesAll, new GetAllQueriesRequest(entityType));
    }

    public getGroupedData(entityType: string, nameProperty: string, aggregateOperator: string = null, aggregateProperty: string = null, filter: string = null): Promise<any> {
        const uri = "/odata/" + entityType + "/Genesis.GroupBy(column='" + nameProperty + "',propertyForAggregation='" + aggregateProperty + "',aggregationType='" + aggregateOperator + "',groupLimit=null)" + (filter ? filter : '');

        return new Promise<any>((resolve, reject) => {
            this.getData(uri, null)
                .then((data: ODataResponse<any>) => {
                    resolve(data.value);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public updateODataEntities(entityType: string, selectedIds: number[], filtersJson: string, toggleAll: boolean, fields: Field[]): Promise<UpdateODataResponse> {
        const uri = "/odata/" + entityType + "/UpdateODataEntities";

        return this.requestClient.post(uri, {}, { selectedIds, filtersJson, toggleAll, fields });
    }

    public getGroupedDataUri(uri: string, identifier: string = null): Promise<any> {

        return new Promise<any>((resolve, reject) => {
            this.getData(uri, identifier)
                .then((data: ODataResponse<any>) => {
                    resolve(data.value);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public persistQueries(query: Query, assignmentsToAdd: QueryAssignmentBulkItem, assignmentsToRemove: QueryAssignmentBulkItem): Promise<PersistQueryResponse> {
        const request: PersistQueryRequest = {
            queryId: query ? query.queryId : null,
            queryBody: query ? query.queryBody : null,
            columns: query ? query.columns : null,
            queryName: query ? query.queryName : null,
            description: query ? query.description : null,
            assignmentsToAdd: assignmentsToAdd,
            assignmentsToRemove: assignmentsToRemove
        }

        return this.requestClient.post(Urls.Queries, {}, request);
    }

    public deleteQuery(queryId: number): Promise<DeleteQueryResponse> {
        return this.requestClient.delete(Urls.DeleteQuery, {}, { queryId: queryId });
    }

    public cloneQuery(queryId: number, queryName: string, shareGlobally: boolean) {
        const request: CloneQueryRequest = {
            queryId: queryId,
            queryName: queryName,
            shareGlobally: shareGlobally
        };

        return this.requestClient.post(Urls.CloneQuery, {}, request);
    }

    public addQueryAssignment(queryId: number, categoryId: number, category: QueryAssignmentEnum, isDefault: boolean) {
        const request: AddQueryAssignmentRequest = {
            queryId: queryId,
            categoryId: categoryId,
            category: category,
            isDefault: isDefault
        };

        return this.requestClient.post(Urls.AddQueryAssignment, {}, request);
    }

    public deleteAssignmentsForQuery(queryId: number) {
        const request: DeleteAssignmentsForQueryRequest = {
            queryId: queryId
        }

        return this.requestClient.delete(Urls.DeleteAssignmentsForQuery, {}, request)
    }

    public getQueryAssignments(queryId: number) {
        return this.requestClient.get(Urls.GetQueryAssignments, { queryId: queryId })
    }

    public getUserQueries(entityType: string) {
        return this.requestClient.get(Urls.GetUserQueries, new GetQueriesRequest(entityType));
    }

    public getAssignedQueries(entityType: string) {
        return this.requestClient.get(Urls.GetAssignedQueries, new GetQueriesRequest(entityType));
    }

    private mapOperators(items: EntityMetaDataModelOperator[]): GetOperatorsResponse {
        const results: { [index: string]: Array<EntityMetaDataModelOperator> } = {};
        const resultsForCollection: { [index: string]: Array<EntityMetaDataModelOperator> } = {};

        const values = EnumUtilities.getValues(DataType);
        for (const value of values) {
            results[value] = new Array<EntityMetaDataModelOperator>();
            resultsForCollection[value] = new Array<EntityMetaDataModelOperator>();
        }

        //add all the items in a 'dictionary type' so that we can access them easily
        for (const item of items) {

            for (const type of item.supportedTypes) {
                results[type].push(item);
            }

            for (const type of item.supportedTypesForCollectionProperties) {
                resultsForCollection[type].push(item);
            }
        }

        return {
            operatorsForSingleValues: results,
            operatorsForCollections: resultsForCollection
        };
    }

    public getNameProperties(entityType: string): Promise<EntityFilterPropertyModel[]> {
        return new Promise<EntityFilterPropertyModel[]>((resolve, reject) => {
            this.getEntityModels()
                .then((entityMetaData) => {
                    if (entityMetaData) {
                        if (entityType) {
                            let result: EntityFilterPropertyModel[] = [];

                            for (const row of entityMetaData) {
                                if (row.uri === entityType) {
                                    result = row.filterProperties;
                                }
                            }
                            resolve(result);
                        }
                    }
                    resolve([]);
                })
                .catch((error) => {
                    reject(error);
                });
        });

    }

    public odataModelToString(filter: string, format: string | ((filter: string, item: any, isAuthenticated: boolean, isFilter: boolean) => string), properties: string, config: EntityTypeConfigurationModel, item, isFilter: boolean = false): string {

        if (!item) {
            return null;
        }

        let propertiesArray = properties ? properties.split(',') : null;

        if (!item) {
            return null;
        }

        let finalFormat = typeof format == "function" ? format(filter, item, !!this.tokenClient.currentUser, isFilter) : format;

        if (config && (!format || format == "")) {
            if (config.format && typeof config.format === "string") {
                finalFormat = config.format;
            } else if (config.format && typeof config.format === "function") {
                finalFormat = config.format(filter, item, !!this.tokenClient.currentUser, isFilter);
            } else {
                finalFormat = "{0}";
            }
        }
        if (config && config.properties && (!properties || properties == "")) {
            propertiesArray = config.properties.split(',');
        }
        if (propertiesArray && propertiesArray.length > 0) {
            for (let i = 0; i < propertiesArray.length; i++) {
                const index = "{" + i + "}";
                const regex = new RegExp(StringUtilities.escapeRegExp(index), 'g');
                const str = item[propertiesArray[i]];
                finalFormat = finalFormat.replace(regex, str ? str : "");
            }
        }

        return this.decode(item && Object.keys(item).length !== 0 ? finalFormat : "");
    }


    decode(text: string) {
        const decoded = $('<div/>').html(text).get(0).innerHTML;
        return decoded || null;
    }
}
